/**
 * Client-side image downscaler/recompressor for logo uploads.
 *
 * Takes any browser-decodable image (PNG/JPEG/WebP/GIF/BMP/SVG) and returns a
 * base64 data URI guaranteed to be a PNG or JPEG under `maxBytes`. Transparency
 * is preserved for non-JPEG sources (PNG output); opaque/photographic sources
 * use JPEG for better compression. The backend re-validates the result.
 */

const DEFAULT_MAX_BYTES = 200 * 1024;
const DEFAULT_MAX_DIM = 1000;          // logos render tiny on the PDF; cap source size
const HARD_INPUT_LIMIT = 40 * 1024 * 1024; // refuse absurd inputs outright

export async function compressImageToDataUri(
  file,
  { maxBytes = DEFAULT_MAX_BYTES, maxDim = DEFAULT_MAX_DIM } = {}
) {
  if (!file.type.startsWith("image/")) {
    throw new Error("That file is not an image.");
  }
  if (file.size > HARD_INPUT_LIMIT) {
    throw new Error("That image is too large to process.");
  }

  const img = await loadImage(await readAsDataURL(file));
  // Keep alpha for anything that might be transparent; JPEG only for known-opaque sources.
  const opaqueSource = file.type === "image/jpeg" || file.type === "image/bmp";
  const outType = opaqueSource ? "image/jpeg" : "image/png";

  let scale = Math.min(1, maxDim / Math.max(img.naturalWidth || 1, img.naturalHeight || 1));

  for (let i = 0; i < 12; i++) {
    const w = Math.max(1, Math.round((img.naturalWidth || maxDim) * scale));
    const h = Math.max(1, Math.round((img.naturalHeight || maxDim) * scale));

    if (outType === "image/jpeg") {
      for (const q of [0.92, 0.8, 0.7, 0.6, 0.5]) {
        const blob = await drawToBlob(img, w, h, "image/jpeg", q, "#ffffff");
        if (blob.size <= maxBytes) return blobToDataUri(blob);
      }
    } else {
      const blob = await drawToBlob(img, w, h, "image/png");
      if (blob.size <= maxBytes) return blobToDataUri(blob);
    }

    if (Math.max(w, h) <= 48) break; // can't meaningfully shrink further
    scale *= 0.82;
  }

  // Last resort: flatten onto white and hard-compress as a small JPEG.
  const m = Math.min(1, 320 / Math.max(img.naturalWidth || 320, img.naturalHeight || 320));
  const blob = await drawToBlob(
    img,
    Math.max(1, Math.round((img.naturalWidth || 320) * m)),
    Math.max(1, Math.round((img.naturalHeight || 320) * m)),
    "image/jpeg",
    0.5,
    "#ffffff"
  );
  if (blob.size <= maxBytes) return blobToDataUri(blob);
  throw new Error("Could not shrink this image under 200 KB. Try a simpler image.");
}

function readAsDataURL(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result));
    r.onerror = () => reject(new Error("Could not read that file."));
    r.readAsDataURL(file);
  });
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("That image could not be loaded."));
    img.src = src;
  });
}

function drawToBlob(img, w, h, type, quality, background) {
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  if (background) {
    ctx.fillStyle = background; // JPEG has no alpha - flatten transparency onto white
    ctx.fillRect(0, 0, w, h);
  }
  ctx.drawImage(img, 0, 0, w, h);
  return new Promise((resolve, reject) =>
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error("Image encoding failed."))),
      type,
      quality
    )
  );
}

function blobToDataUri(blob) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result));
    r.onerror = () => reject(new Error("Could not encode the image."));
    r.readAsDataURL(blob);
  });
}
